import React, { FC, useMemo, useState } from 'react';
import { Button, ExtendedTheme, Link, Snackbar, TextBlock } from 'neon-design-system';
import { CreateChannelSubscriptionInput, GET_USER_PREMIUM_CHANNEL_SUBSCRIPTIONS } from '../../../gql/users';
import { Channel, ChannelSubscription } from '../../../shared/types';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { createStyles, makeStyles } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { getChannelSubscriptionInterestConfirmLink, getChannelSubscriptionLink } from '../../../utils/link.utils';
import { CREATE_OPEN_FREE_CHANNEL_SUBSCRIPTION, REQUEST_FREE_CLOSED_CHANNEL_SUBSCRIPTION } from '../../../gql/subscriptions';
import { useUser } from '../../providers/UserProvider';
import { useLogger } from '../../providers/logger/Logger';
import { LogAction, LogLevel } from '../../providers/logger/logger-types';
import { getDisplayProperties } from '../../../shared/channelSubscription';
import { formatDisplayDate } from '../../../utils/date.utils';
import { baseUrls } from '../../../utils/baseUrls.utils';
import { useRequestFreeTrial } from '../../hooks/use-request-free-trial';

export interface ChannelSubscriptionButtonProps {
	channel: Channel;
	disclaimer?: boolean;
	disclaimerText?: JSX.Element | string;
	size?: 'large' | 'small';
	colorType?: 'primary' | 'secondary' | 'grey';
	align?: 'center' | 'left';
	linkClassName?: string;
	onUpdate?: () => void;
}

const useStyles = makeStyles<ExtendedTheme, Partial<ChannelSubscriptionButtonProps>>((theme: ExtendedTheme) => createStyles({
	disclaimer: {
		color: theme.themeColors.blueGrey[200],
		maxWidth: 'fit-content',
		textAlign: 'center',
		'& a': { fontSize: 'inherit' }
	},
	buttonWrapper: ({ align }) => ({
		display: 'flex',
		gap: 8,
		alignItems: align === 'center' ? 'center' : 'flex-start',
		flexDirection: 'column'
	}),
	buttons: {
		display: 'flex',
		gap: theme.spacing(1)
	}
}));

export const ChannelSubscriptionButton: FC<ChannelSubscriptionButtonProps> = ({
	channel,
	disclaimer = true,
	size = 'large',
	align = 'center',
	onUpdate = () => {}
}) => {
	const { userDetails } = useUser();
	const { printLog } = useLogger()
	const history = useHistory();
	const [freeOpenAlertVisible, setFreeOpenAlertVisible] = useState(false);
	const [freeClosedRequestAlertVisible, setFreeClosedRequestAlertVisible] = useState(false);
	// todo: a future enhancement would be to update the mutation so Appolo cache is automoatically updated
	const { data, loading, refetch } = useQuery(GET_USER_PREMIUM_CHANNEL_SUBSCRIPTIONS, { fetchPolicy: 'network-only' });
	const [createFreeChannelSubscription, {loading: createFreeOpenSubscriptionLoading}] = useMutation<ChannelSubscription, { item: CreateChannelSubscriptionInput }>(CREATE_OPEN_FREE_CHANNEL_SUBSCRIPTION);
	const [requestFreeClosedChannelSubscription, {loading: requestFreeClosedChannelSubscriptionLoading}] = useMutation<ChannelSubscription, { channelId: string }>(REQUEST_FREE_CLOSED_CHANNEL_SUBSCRIPTION);
	const [requestTrial, loadingRequestTrial] = useRequestFreeTrial();

	const channelSub = useMemo(() => data?.premiumChannelSubscriptions?.find((pc: ChannelSubscription) => pc.channelId === channel._id), [data]);

	const {
		freeClosedRequestRejected,
		freeClosedRequestPending,
		isFreeOpenChannel,
		isFreeClosedChannel,
		isPremiumChannel,
		isSubscriptionActive,
		isTrialSubscriptionPending,
		isTrialSubscriptionActive
	} = useMemo(() => getDisplayProperties(channelSub, channel), [channelSub, channel]);

	const classes = useStyles({ align });

	const subscriptionHandler = () => {
		printLog(
			LogLevel.INFO,
			LogAction.RegisteredInterestPremiumChannel,
			LogAction.RegisteredInterestPremiumChannel,
			{ channelId: channel._id, channelName: channel.name, userEmail: userDetails?.email }
		);

		window.Appcues?.track('PremiumChannelSubscribeClick', {
			channelName: channel.name,
			userEmail: userDetails?.email
		});

		if (isPremiumChannel) {
			requestTrial(channel._id).then(
				() => history.push(getChannelSubscriptionInterestConfirmLink(channel._id) + '?trial')
			);
		}

		if (isFreeOpenChannel) {
			createFreeChannelSubscription({
				variables: {
					item: {
						channelId: channel._id
					}
				}
			}).then(() => {
				refetch();
				onUpdate();
				setFreeOpenAlertVisible(true);
			});
		}

		if (isFreeClosedChannel) {
			requestFreeClosedChannelSubscription({
				variables: {
					channelId: channel._id
				}
			}).then(() => {
				refetch();
				onUpdate();
				setFreeClosedRequestAlertVisible(true);
			})
		}
	}

	const buttonText = () => {
		if (isTrialSubscriptionPending) return 'Trial request pending';

		if (isFreeOpenChannel) return 'Subscribe for free';

		if (freeClosedRequestPending) return 'Access request pending';

		if (isFreeClosedChannel) return 'Request access for free';

		return 'Request free trial';
	}

	if (loading) {
		return <></>;
	}

	const disclaimerContent = isSubscriptionActive && !isPremiumChannel
		? ''
		: isFreeOpenChannel
			? <>Get email notifications when new <br />articles are published</>
			: isFreeClosedChannel
				? (
					freeClosedRequestPending
						? <>Your request is currently under review,<br />check your inbox for updates</>
						: <>Become a channel subscriber to get free<br />and unlimited access to all articles</>
				) : isTrialSubscriptionActive
					? `Your free trial access ends on ${formatDisplayDate(channelSub.cancelAt, false)}`
					: isTrialSubscriptionPending
						? <>Don't want to wait? <Link to={getChannelSubscriptionLink(channel._id)}>Buy now</Link></>
						: <>This is a premium subscription model.<br/>Once started, plan automatically renews until cancelled.</>;

	const showPricingOptions = isPremiumChannel && (!isSubscriptionActive || isTrialSubscriptionActive);

	return <>
		<Snackbar
			message="Thanks for becoming a subscriber! You will receive email notifications when new articles are published."
			type="info"
			open={freeOpenAlertVisible}
			onClose={() => setFreeOpenAlertVisible(false)}
			autoHideDuration={5000}
		/>
		<Snackbar
			message="Thanks for requesting acces to the channel. The Channel Owners will review your request. Watch your inbox for further communications!"
			type="info"
			open={freeClosedRequestAlertVisible}
			onClose={() => setFreeClosedRequestAlertVisible(false)}
			autoHideDuration={5000}
		/>

		<div className={classes.buttonWrapper}>
			<div className={classes.buttons}>
				{((isPremiumChannel && (!channelSub || isTrialSubscriptionPending)) || (!isPremiumChannel && !isSubscriptionActive)) && <Button
					id="subscribe-cta"
					colorType="primary"
					size={size}
					onClick={subscriptionHandler}
					disabled={isTrialSubscriptionPending || freeClosedRequestRejected || freeClosedRequestPending}
					busy={createFreeOpenSubscriptionLoading || requestFreeClosedChannelSubscriptionLoading || loadingRequestTrial}
				>
					{buttonText()}
				</Button>}
				{showPricingOptions && <Button
					colorType="grey"
					size={size}
					onClick={() => history.push(getChannelSubscriptionLink(channel._id))}>
						See pricing options
				</Button>}
				{isSubscriptionActive && !showPricingOptions && <Button
					colorType="grey"
					size={size}
					onClick={() => history.push(baseUrls.subscriptions)}>
						Manage subscriptions
				</Button>}
			</div>
			{disclaimer && <TextBlock styleName="caption2" className={classes.disclaimer}>{disclaimerContent}</TextBlock>}
		</div>
	</>;
}
